/**
 * Auth-sync utility.
 *
 * Mirrors §6.4 of the Keycloak integration documentation, adapted for
 * Notify: only the two backend roles exist (`SuperUser`, `Maintenance` —
 * see `Notify_Admin/Shared/Enums/RoleEnum.cs`) and there is no
 * hotel/chain scope to extract, so the `hotel-id-*` / `chain-id-*`
 * prefix-stripping logic from the doc is intentionally omitted.
 */
import type Keycloak from "keycloak-js";

/**
 * Keycloak client-role → Notify app-role. Both Keycloak conventional
 * lowercase names and the PascalCase names that match the backend enum
 * directly are accepted, so the realm admin can use either casing when
 * creating roles on the `dev-notify` client.
 */
const ROLE_MAP: Record<string, string> = {
  superuser: "SuperUser",
  SuperUser: "SuperUser",
  admin: "SuperUser",
  maintenance: "Maintenance",
  Maintenance: "Maintenance",
};

/**
 * Priority order when a token carries both roles — SuperUser wins.
 */
const ROLE_PRIORITY: readonly string[] = ["SuperUser", "Maintenance"];

const STORAGE_KEYS = [
  "token",
  "token_expiration",
  "user_id",
  "username",
  "email",
  "role",
  "auth_method",
] as const;

export function syncAuthToLocalStorage(sso: Keycloak): void {
  const token = sso.token;
  const parsed = sso.tokenParsed as Record<string, unknown> | undefined;
  if (!token || !parsed) {
    clearAuthData();
    return;
  }

  const clientId =
    sso.clientId ?? (import.meta.env.VITE_AUTH_CLIENT_ID as string);

  // Roles can appear on the active client (`resource_access[clientId].roles`)
  // or at the realm level (`realm_access.roles`). Read both — the realm
  // admin can configure either, and we want to accept whichever they chose.
  const resourceAccess = parsed.resource_access as
    | Record<string, { roles?: string[] }>
    | undefined;
  const realmAccess = parsed.realm_access as { roles?: string[] } | undefined;

  const azp = parsed.azp as string | undefined;
  const roles = new Set<string>([
    ...(azp ? (resourceAccess?.[azp]?.roles ?? []) : []),
    ...(resourceAccess?.[clientId]?.roles ?? []),
    ...(realmAccess?.roles ?? []),
  ]);

  // Map the raw Keycloak roles through ROLE_MAP, then pick the highest
  // priority app role present. `find(Boolean)` (as in the doc) would
  // give the first-mapped one which is order-dependent; explicit priority
  // is safer when a user has both `superuser` and `maintenance`.
  const mapped = Array.from(roles)
    .map((r) => ROLE_MAP[r])
    .filter((r): r is string => !!r);
  const role =
    ROLE_PRIORITY.find((candidate) => mapped.includes(candidate)) ?? null;

  const exp = typeof parsed.exp === "number" ? parsed.exp : 0;
  const expiresIn = exp ? exp - Math.floor(Date.now() / 1000) : 3600;

  localStorage.setItem("authToken", token);
  localStorage.setItem(
    "token_expiration",
    String(Date.now() + expiresIn * 1000),
  );
  localStorage.setItem("user_id", (parsed.sub as string) ?? "");
  localStorage.setItem(
    "username",
    (parsed.name as string) ?? (parsed.preferred_username as string) ?? "",
  );
  localStorage.setItem("email", (parsed.email as string) ?? "");
  if (role) {
    localStorage.setItem("role", role);
  } else {
    localStorage.removeItem("role");
    console.warn(
      "[SSO] No Notify role found in token. Roles seen:",
      Array.from(roles),
      "— expected one of:",
      Object.keys(ROLE_MAP),
    );
  }
  localStorage.setItem("auth_method", "sso");
}

export function clearAuthData(): void {
  for (const k of STORAGE_KEYS) localStorage.removeItem(k);
}
